class GwpCore extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.cart = [];
    this.offers = [];
    this.eventListeners = [];
    this.applyOffers = [];
    this.removeOffers = [];
    this.offerStatus = [];
    this.cartSections = [];
    this.callback = window.raftelGwpCallback;
  }

  async connectedCallback() {
    await this.fetchCart();
    // Get offers
    const offers = this.querySelector("noscript[data-gwp-offers]");
    this.offers = JSON.parse(offers.textContent);
    // Get event listeners
    const eventListeners = this.querySelector("noscript[data-gwp-listeners]");
    this.eventListeners = JSON.parse(eventListeners.textContent);
    // Get cart sections
    const cartSections = this.querySelector("noscript[data-gwp-cart-sections]");
    this.cartSections = JSON.parse(cartSections.textContent);

    this.debug({ data: this.cart });
    this.debug({ data: this.offers });
    this.debug({ data: this.eventListeners });
    this.debug({ data: this.cartSections });

    this.subscribeToEvents();
    this.validateOffers();
  }

  subscribeToEvents() {
    this.eventListeners.forEach((eventListener) => {
      document.addEventListener(eventListener, this.validateOffers.bind(this));
    });
  }

  async validateOffers() {
    if (this.offers.length == 0) {
      console.log("GWP:: No offers to validate");
      return;
    }
    await this.fetchCart();
    this.offerStatus = [];
    this.offers.forEach((offer) => {
      if (offer.enabled) {
        this.validateOffer(offer);
      } else {
        this.addToRemoveOffers(offer);
      }
    });
    if (this.applyOffers.length > 0) {
      this.debug({ type: "Apply offers", data: this.applyOffers });
      this.addOffersToCart(this.applyOffers);
    }
    if (this.removeOffers.length > 0) {
      this.debug({ type: "Remove offers", data: this.removeOffers });
      this.removeOffersToCart(this.removeOffers);
    }
    // sort offers by threshold
    this.offerStatus.sort((a, b) => a.threshold - b.threshold);
    window.raftelGwpOffers = this.offerStatus;
    document.dispatchEvent(
      new CustomEvent("gwp:offers:updated", {
        detail: { offers: this.offerStatus, total: this.cart.total_price },
        bubbles: true,
      })
    );
  }

  async validateOffer(offer) {
    this.debug({ type: "Validate offer", data: offer });
    const offerStatus = this.getOfferStatus(offer);
    this.offerStatus.push(offerStatus);
    if (offerStatus.canApply) {
      if (!offerStatus.alreadyInCart) {
        if (offer.product.available) {
          this.addToApplyOffers(offer);
        }
      }
    } else {
      if (offerStatus.alreadyInCart) {
        this.addToRemoveOffers(offer);
      }
    }
  }

  addToApplyOffers(offer) {
    this.debug({ type: "Add to apply offers", data: offer });
    this.applyOffers.push({
      id: offer.product.vid,
      quantity: 1,
      properties: {
        _gwp_enabled: offer.product.vid,
      },
    });
  }

  addToRemoveOffers(offer) {
    this.debug({ type: "Add to remove offers", data: offer });
    if (offer.product.vid) {
      this.removeOffers.push(offer.product.vid);
    }
  }

  isAvailableToOffer(offer) {
    const amount = parseFloat(offer.condition.threshold);
    if (this.cart.items.length == 0) {
      return false;
    }
    const itemsWithoutOffer = this.cart.items.filter((item) => !item?.properties?._gwp_enabled);
    return (
      itemsWithoutOffer.reduce((acc, item) => acc + item.line_price / 100, 0) >= amount
    );
  }

  isOfferAlreadyInCart(pid) {
    return this.cart.items.some((item) => item?.properties?._gwp_enabled == pid);
  }

  getOfferStatus(offer) {
    return {
      id: offer.product.id,
      vid: offer.product.vid,
      enabled: offer.enabled,
      title: offer.product.title,
      threshold: parseFloat(offer.condition.threshold),
      canApply: this.isAvailableToOffer(offer),
      alreadyInCart: this.isOfferAlreadyInCart(offer.product.vid),
      pendingAmount: this.getPendingAmount(offer),
    };
  }

  getPendingAmount(offer) {
    const amount = parseFloat(offer.condition.threshold);
    return amount - this.cart.items.reduce((acc, item) => acc + item.line_price / 100, 0);
  }

  // Fetch cart
  async fetchCart() {
    const response = await fetch("/cart.json");
    const cart = await response.json();
    this.cart = cart;
  }

  // Add offers to cart
  async addOffersToCart(items) {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items,
        sections: this.cartSections,
      }),
    });

    this.applyOffers = [];
    if (this.callback) {
      const json = await response.json();
      this.callback(json);
    } else {
      window.location.reload();
    }
  }

  // Remove all offers from cart
  async removeOffersToCart(items) {
    const itemsToRemove = this.cart.items.filter((item) => items.includes(item?.properties?._gwp_enabled));
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        updates: itemsToRemove.reduce((acc, item) => {
          acc[item.id] = 0;
          return acc;
        }, {}),
        sections: this.cartSections,
      }),
    });

    this.removeOffers = [];
    if (this.callback) {
      const json = await response.json();
      if (itemsToRemove?.length > 0) {
        this.callback(json);
      }
    } else {
      window.location.reload();
    }
  }

  async removeOfferFromCart(pid) {
    const itemsToRemove = this.cart.items.filter((item) => item?.properties?._gwp_enabled == pid);
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        updates: itemsToRemove.reduce((acc, item) => {
          acc[item.id] = 0;
          return acc;
        }, {}),
      }),
    });

    this.removeOffers = [];
    if (this.callback) {
      const json = await response.json();
      this.callback(json);
    } else {
      window.location.reload();
    }
  }

  debug({ type = "Debug", data }) {
    if (this.getAttribute("debug") === "true") {
      console.log(`RaftelGWP - ${type}`, data);
    }
  }

  disconnectedCallback() {
    this.eventListeners.forEach((eventListener) => {
      document.removeEventListener(eventListener, this.validateOffers.bind(this));
    });
  }
}

customElements.define("gwp-core", GwpCore);